'use client';

import { memo, useEffect, useRef } from 'react';
import { useRetornoAds } from './context';
import {
  TRACK_EVENTS,
  useTrackingEvents,
} from '@mais-retorno-front/hooks/tracking-events';
import Link from 'next/link';
import { Card } from '@core/design-system/card/app';
import { Profit } from './components/profit';
import { truncateText } from '@core/design-system/utils/format-number';
import { RETORNO_ADS_EVENT_ACTION, RETORNO_ADS_TYPE } from './gtm.constant';
import { cn } from '@core/design-system/tailwind/lib/utils';
import { HighlightsText } from './highlights-text';

const createId = (cnpj: string) => `RETORNO_ADS_${cnpj}`;

export const RetornoAdsAssetCard = memo(() => {
  const { retornoAds } = useRetornoAds();

  const adsRef = useRef<HTMLDivElement>(null);
  const { trackEvent } = useTrackingEvents();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!retornoAds.length) return;

    /**
     * Aplica o evento de visualizado apenas se o usuario visualizar o banner
     */
    const observer = new IntersectionObserver(
      ([elementSubscription]) => {
        if (elementSubscription.isIntersecting === true) {
          retornoAds.map((item) =>
            trackEvent(TRACK_EVENTS.RETORNO_ADS, {
              retorno_ad_id: item.cnpj,
              retorno_ad_event_action: RETORNO_ADS_EVENT_ACTION.VIEW,
              retorno_ad_type: RETORNO_ADS_TYPE.CARD,
            })
          );

          observer?.unobserve(adsRef?.current);
        }
      },
      { threshold: [0.5] }
    );

    if (adsRef.current) {
      observer.observe(adsRef.current);
    }

    return () => {
      if (adsRef.current) {
        observer.disconnect();
      }
    };
  }, [adsRef, retornoAds]);

  if (!retornoAds.length) return null;

  return (
    <section className="print:hidden">
      <HighlightsText />

      <div className="grid grid-cols-12 gap-2" ref={adsRef}>
        {retornoAds.map((sheet, index) => (
          <Link
            id={createId(sheet.cnpj)}
            title={`Ver lâmina do fundo ${sheet.nicename}`}
            key={`${sheet.slug}-${index}`}
            onClick={() => {
              trackEvent(TRACK_EVENTS.RETORNO_ADS, {
                retorno_ad_id: sheet.cnpj,
                retorno_ad_event_action: RETORNO_ADS_EVENT_ACTION.CLICK,
                retorno_ad_type: RETORNO_ADS_TYPE.CARD,
              });
            }}
            href={sheet.slug}
            prefetch={false}
            className={cn(
              'col-span-12 sm:col-span-6 lg:col-span-3 hover:no-underline',
              retornoAds.length <= 3 ? 'lg:col-span-4' : 'lg:col-span-3'
            )}
          >
            <Card className="p-3 h-full flex flex-col justify-between hover:bg-[#e5e5e5] transition-all duration-300">
              <div className="flex justify-between md:mb-2 items-center">
                <p className="text-sm font-medium">
                  {truncateText(sheet?.nicename)}
                </p>

                <Profit
                  profitability={sheet.profitability}
                  isPositive={sheet.profitability_is_positive}
                  className="block sm:hidden"
                />
              </div>

              <div className="hidden sm:flex justify-between mt-3 gap-1 leading-5 flex-wrap">
                {sheet.networth && (
                  <div className="space-y-1">
                    <p className="text-sm font-semibold">{sheet.networth}</p>

                    <span className="text-2xs font-light">
                      Patrimônio Líquido
                    </span>
                  </div>
                )}

                {!sheet.networth && sheet.volatility && (
                  <div className="space-y-1">
                    <p className="text-sm font-semibold">{sheet.volatility}</p>

                    <span className="text-2xs font-light">
                      Volatilidade 12M
                    </span>
                  </div>
                )}

                {sheet.quota_holders && (
                  <div className="space-y-1">
                    <p className="text-sm font-semibold">
                      {sheet.quota_holders}
                    </p>

                    <span className="text-2xs font-light">Cotistas</span>
                  </div>
                )}

                <Profit
                  profitability={sheet.profitability}
                  isPositive={sheet.profitability_is_positive}
                />
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
});
